import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  documentId,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { AdminTabs } from './VoiceAdmin';

type NotificationError = {
  code?: string;
  message?: string;
};

type NotificationRecipient = {
  userId?: string | null;
  username?: string;
  displayName?: string;
  email?: string;
  tokenPreview?: string | null;
  deliveryChannel?: string;
  success?: boolean;
  messageId?: string | null;
  error?: NotificationError | string | null;
};

type NotificationLog = {
  id: string;
  title?: string;
  body?: string;
  notificationType?: string;
  type?: string;
  functionName?: string;
  success?: boolean;
  messageId?: string | null;
  error?: NotificationError | null;
  timestamp?: Timestamp;
  timestampEpoch?: number;
  createdAt?: Timestamp | Date | number | null;
  sentAt?: Timestamp | Date | number | null;
  multicast?: boolean;
  totalTokens?: number;
  successCount?: number;
  failureCount?: number;
  recipients?: Array<NotificationRecipient | string>;
  individualResults?: Array<NotificationRecipient>;
  dataPayload?: Record<string, unknown>;
  additionalContext?: Record<string, unknown>;
  userId?: string;
};

type UserDirectoryEntry = {
  userId: string;
  username?: string;
  displayName?: string;
  email?: string;
};

type DisplayRecipient = NotificationRecipient & Omit<UserDirectoryEntry, 'userId'> & {
  userId?: string | null;
};

const chunkItems = <T,>(items: T[], size: number) => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const date = new Date(value < 1_000_000_000_000 ? value * 1000 : value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as Timestamp).toDate === 'function') {
    return (value as Timestamp).toDate();
  }
  return null;
};

const formatTimestamp = (...values: unknown[]) => {
  for (const value of values) {
    const date = toDate(value);
    if (date) return date.toLocaleString();
  }
  return 'N/A';
};

const humanizeType = (value: string) =>
  value
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getLogType = (log: NotificationLog) => log.notificationType || log.type || 'unknown';
const getLogTitle = (log: NotificationLog) => log.title || humanizeType(getLogType(log));
const getLogBody = (log: NotificationLog) => log.body || 'No message body recorded.';

const normalizeRecipient = (recipient: NotificationRecipient | string): NotificationRecipient | null => {
  if (typeof recipient === 'string') {
    if (recipient.includes('@')) return { email: recipient, deliveryChannel: 'email' };
    return { tokenPreview: recipient, deliveryChannel: 'push' };
  }
  if (!recipient || typeof recipient !== 'object') return null;
  return recipient;
};

const normalizeError = (error: NotificationRecipient['error'] | NotificationLog['error']) => {
  if (!error) return null;
  if (typeof error === 'string') return { code: 'ERROR', message: error };
  return error;
};

const getRecipients = (log: NotificationLog): NotificationRecipient[] => {
  const explicit = Array.isArray(log.recipients)
    ? (log.recipients.map(normalizeRecipient).filter(Boolean) as NotificationRecipient[])
    : [];
  if (explicit.length > 0) return explicit;

  if (Array.isArray(log.individualResults) && log.individualResults.length > 0) {
    return log.individualResults.map((result) => ({
      ...result,
      deliveryChannel: result.deliveryChannel || 'push',
    }));
  }

  const context = log.additionalContext || {};
  const fallback: NotificationRecipient = {};
  if (typeof context.userId === 'string') fallback.userId = context.userId;
  if (typeof context.targetUid === 'string') fallback.userId = context.targetUid;
  if (typeof context.email === 'string') fallback.email = context.email;
  if (log.userId) fallback.userId = log.userId;
  if (typeof log.success === 'boolean') fallback.success = log.success;
  if (log.messageId) fallback.messageId = log.messageId;
  if (log.error) fallback.error = log.error;
  fallback.deliveryChannel = 'push';

  return Object.values(fallback).some(Boolean) ? [fallback] : [];
};

const buildUserDirectoryEntry = (userId: string, data: Record<string, unknown>): UserDirectoryEntry => ({
  userId,
  username: typeof data.username === 'string' ? data.username : '',
  displayName: typeof data.name === 'string'
    ? data.name
    : typeof data.displayName === 'string'
      ? data.displayName
      : '',
  email: typeof data.email === 'string' ? data.email : '',
});

const recipientKeys = (recipient: NotificationRecipient) => {
  const keys: string[] = [];
  if (recipient.userId) keys.push(`user:${recipient.userId}`);
  if (recipient.email) keys.push(`email:${recipient.email.toLowerCase()}`);
  return keys;
};

const displayName = (recipient: Partial<DisplayRecipient>) =>
  recipient.username
    ? `@${recipient.username}`
    : recipient.displayName || recipient.email || recipient.userId || 'Unknown recipient';

const secondaryLine = (recipient: Partial<DisplayRecipient>) => {
  if (recipient.email) return recipient.email;
  if (recipient.displayName && recipient.username) return recipient.displayName;
  if (recipient.userId) return `User ID: ${recipient.userId}`;
  return recipient.deliveryChannel || 'Recipient details unavailable';
};

const statusTone = (success?: boolean) => {
  if (success === true) return 'bg-green-50 border-green-200 text-green-700';
  if (success === false) return 'bg-red-50 border-red-200 text-red-700';
  return 'bg-neutral-100 border-neutral-200 text-neutral-600';
};

const statusLabel = (success?: boolean) => {
  if (success === true) return 'Sent';
  if (success === false) return 'Failed';
  return 'Recorded';
};

export const NotificationLogsAdmin: React.FC = () => {
  const { user, signOutUser } = useAuth();
  const [logs, setLogs] = useState<NotificationLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<NotificationLog | null>(null);
  const [directory, setDirectory] = useState<Record<string, UserDirectoryEntry>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = async () => {
    if (!db) return;
    setLoading(true);
    setError(null);
    try {
      const snapshot = await getDocs(query(collection(db, 'notification-logs'), orderBy('timestampEpoch', 'desc'), limit(150)));
      const next = snapshot.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...(docSnapshot.data() as Omit<NotificationLog, 'id'>),
      }));
      setLogs(next);
      setSelectedLog((current) => {
        if (!current) return next[0] || null;
        return next.find((log) => log.id === current.id) || next[0] || null;
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to load notification logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const resolveRecipients = async () => {
      if (!db || logs.length === 0) {
        setDirectory({});
        return;
      }
      const recipients = logs.flatMap((log) => getRecipients(log));
      const userIds = Array.from(new Set(recipients.map((recipient) => recipient.userId).filter(Boolean) as string[]));
      const emails = Array.from(new Set(recipients.map((recipient) => recipient.email?.toLowerCase()).filter(Boolean) as string[]));
      const next: Record<string, UserDirectoryEntry> = {};
      const addEntry = (entry: UserDirectoryEntry) => {
        next[`user:${entry.userId}`] = entry;
        if (entry.email) next[`email:${entry.email.toLowerCase()}`] = entry;
      };

      try {
        for (const userIdChunk of chunkItems(userIds, 10)) {
          const snapshot = await getDocs(query(collection(db, 'users'), where(documentId(), 'in', userIdChunk)));
          snapshot.forEach((docSnapshot) => addEntry(buildUserDirectoryEntry(docSnapshot.id, docSnapshot.data())));
        }
        for (const emailChunk of chunkItems(emails, 10)) {
          const snapshot = await getDocs(query(collection(db, 'users'), where('email', 'in', emailChunk)));
          snapshot.forEach((docSnapshot) => addEntry(buildUserDirectoryEntry(docSnapshot.id, docSnapshot.data())));
        }
        if (!cancelled) setDirectory(next);
      } catch {
        if (!cancelled) setDirectory(next);
      }
    };

    resolveRecipients();
    return () => {
      cancelled = true;
    };
  }, [logs]);

  const stats = useMemo(() => {
    const sent = logs.filter((log) => log.success === true).length;
    const failed = logs.filter((log) => log.success === false).length;
    const multicast = logs.filter((log) => log.multicast).length;
    return { sent, failed, multicast, total: logs.length };
  }, [logs]);

  const selectedRecipients = selectedLog
    ? getRecipients(selectedLog).map((recipient) => {
        const match = recipientKeys(recipient).map((key) => directory[key]).find(Boolean);
        return { ...match, ...recipient } as DisplayRecipient;
      })
    : [];

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-lg font-bold">Notification Logs</div>
            <div className="text-xs text-neutral-500">
              Delivery attempts written by push and notification workers.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 font-mono">{user?.email}</span>
            <button onClick={signOutUser} className="text-xs text-neutral-600 underline hover:text-neutral-900">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <AdminTabs current="notificationLogs" />
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Logs" value={stats.total.toLocaleString()} />
          <StatCard label="Sent" value={stats.sent.toLocaleString()} />
          <StatCard label="Failed" value={stats.failed.toLocaleString()} />
          <StatCard label="Multicast" value={stats.multicast.toLocaleString()} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">Recent Notifications</div>
                <div className="text-xs text-neutral-500 mt-1">Newest 150 entries from notification-logs.</div>
              </div>
              <button
                onClick={fetchLogs}
                className="rounded-lg bg-neutral-900 px-4 py-2 text-xs font-semibold text-white hover:bg-neutral-800"
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="p-8 text-sm text-neutral-500">Loading notification logs...</div>
            ) : logs.length === 0 ? (
              <div className="p-8 text-sm text-neutral-500">No notification logs found yet.</div>
            ) : (
              <div className="divide-y divide-neutral-100">
                {logs.map((log) => {
                  const recipients = getRecipients(log);
                  return (
                    <button
                      key={log.id}
                      onClick={() => setSelectedLog(log)}
                      className={`w-full text-left px-6 py-4 transition hover:bg-neutral-50 ${
                        selectedLog?.id === log.id ? 'bg-yellow-50/70' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="font-semibold text-neutral-900 truncate">{getLogTitle(log)}</div>
                          <div className="text-sm text-neutral-600 mt-1 line-clamp-2">{getLogBody(log)}</div>
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-neutral-400">
                            <span className="font-mono">{getLogType(log)}</span>
                            {log.functionName ? <span>{log.functionName}</span> : null}
                            <span>{formatTimestamp(log.timestampEpoch, log.timestamp, log.sentAt, log.createdAt)}</span>
                          </div>
                          {recipients.length > 0 ? (
                            <div className="text-xs text-neutral-500 mt-2">
                              Recipient: {recipients.map(displayName).slice(0, 2).join(', ')}
                              {recipients.length > 2 ? ` +${recipients.length - 2} more` : ''}
                            </div>
                          ) : null}
                        </div>
                        <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${statusTone(log.success)}`}>
                          {log.multicast ? `${log.successCount || 0}/${log.totalTokens || 0}` : statusLabel(log.success)}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <aside className="bg-white border border-neutral-200 rounded-xl p-6 h-fit">
            <div className="text-sm font-semibold text-neutral-900 mb-4">Log Details</div>
            {selectedLog ? (
              <div className="space-y-5">
                <Detail label="Title" value={getLogTitle(selectedLog)} />
                <Detail label="Body" value={getLogBody(selectedLog)} multiline />
                <Detail label="Type" value={getLogType(selectedLog)} />
                <Detail label="Function" value={selectedLog.functionName || 'N/A'} />
                <Detail label="Timestamp" value={formatTimestamp(selectedLog.timestampEpoch, selectedLog.timestamp, selectedLog.sentAt, selectedLog.createdAt)} />

                <div>
                  <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold mb-2">Status</div>
                  <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusTone(selectedLog.success)}`}>
                    {statusLabel(selectedLog.success)}
                  </span>
                  {selectedLog.multicast ? (
                    <div className="mt-2 text-xs text-neutral-500">
                      {selectedLog.successCount || 0} sent, {selectedLog.failureCount || 0} failed, {selectedLog.totalTokens || 0} tokens.
                    </div>
                  ) : null}
                </div>

                {selectedRecipients.length > 0 ? (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold mb-2">Recipients</div>
                    <div className="space-y-2">
                      {selectedRecipients.map((recipient, index) => {
                        const errorValue = normalizeError(recipient.error);
                        return (
                          <div key={`${selectedLog.id}-${index}`} className="rounded-lg border border-neutral-200 p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <div className="font-semibold text-sm truncate">{displayName(recipient)}</div>
                                <div className="text-xs text-neutral-500 mt-1 break-all">{secondaryLine(recipient)}</div>
                              </div>
                              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusTone(recipient.success)}`}>
                                {statusLabel(recipient.success)}
                              </span>
                            </div>
                            {recipient.messageId ? (
                              <div className="mt-2 text-[11px] text-neutral-400 break-all">Message: {recipient.messageId}</div>
                            ) : null}
                            {recipient.tokenPreview ? (
                              <div className="mt-1 text-[11px] text-neutral-400 break-all">Token: {recipient.tokenPreview}</div>
                            ) : null}
                            {errorValue?.message ? (
                              <div className="mt-2 rounded bg-red-50 border border-red-100 p-2 text-xs text-red-700">
                                {errorValue.code ? `${errorValue.code}: ` : ''}{errorValue.message}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {selectedLog.error?.message ? (
                  <div className="rounded-lg bg-red-50 border border-red-100 p-3 text-sm text-red-700">
                    {selectedLog.error.code ? `${selectedLog.error.code}: ` : ''}{selectedLog.error.message}
                  </div>
                ) : null}

                <JsonBlock label="Data Payload" value={selectedLog.dataPayload || {}} />
                <JsonBlock label="Context" value={selectedLog.additionalContext || {}} />
              </div>
            ) : (
              <div className="text-sm text-neutral-500">Select a notification log to inspect it.</div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white border border-neutral-200 rounded-xl px-4 py-3">
    <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold">{label}</div>
    <div className="text-2xl font-bold mt-1">{value}</div>
  </div>
);

const Detail: React.FC<{ label: string; value: string; multiline?: boolean }> = ({ label, value, multiline }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold mb-1">{label}</div>
    <div className={`text-sm text-neutral-700 ${multiline ? 'whitespace-pre-wrap' : 'break-all'}`}>{value}</div>
  </div>
);

const JsonBlock: React.FC<{ label: string; value: unknown }> = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold mb-2">{label}</div>
    <pre className="max-h-56 overflow-auto rounded-lg bg-neutral-950 p-3 text-xs text-neutral-100 whitespace-pre-wrap break-words">
      {JSON.stringify(value, null, 2)}
    </pre>
  </div>
);
